/**
 * Токен доступа к данным ученика и вход по почте.
 *
 * Сервер выдаёт его по ключу устройства (`deviceKey` в сторе) и держит
 * месяц; клиент хранит токен в `localStorage`, чтобы закрытое приложение
 * не просило вход заново. Новый токен берётся по ключу устройства, когда
 * сохранённый истёк (за минуту до этого) или сервер ответил 401; выход из
 * аккаунта стирает и токен, и ключ.
 *
 * Аккаунт открывается только входом по почте (`requestLoginCode` →
 * `verifyLoginCode`) с принятыми соглашениями. До входа ученик — гость:
 * ключа нет, токен не запрашивается (`AUTH_REQUIRED` без похода на сервер),
 * и аккаунт в базе не заводится. Онбординг гость проходит без сервера.
 *
 * Анонимный аккаунт из прошлых версий (ключ без почты) при входе вливается
 * в аккаунт с почтой. Аккаунт из времён до токенов без ключа не
 * закрепляется: одного `userId` мало, чтобы доказать владение.
 */

import type { AuthSession, Consents, LanguageCode, OpenSession } from '@aptile/shared';

import { logger } from '../lib/logger';
import { resetAnalytics } from '../services/posthogService';
import { useUserStore } from '../store/userStore';
import { ApiClientError, apiRequest, configureAuth, jsonBody } from './client';

/** Токен меняется заранее: запрос не должен уйти с истекающим. */
const REFRESH_MARGIN_MS = 60_000;

/**
 * Токен переживает перезапуск приложения. Лежит отдельно от стора: это
 * не профиль, а пропуск — его стирает только выход из аккаунта (или
 * отзыв ключа устройства на сервере).
 */
const TOKEN_KEY = 'aptile-auth-token';

interface StoredToken {
  readonly userId: string;
  readonly token: string;
  readonly expiresAt: number;
}

let current: StoredToken | null = null;
let pending: Promise<string> | null = null;

/** Сохранённый токен этого ученика; чужой или истёкший не берём. */
function readToken(userId: string): StoredToken | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (raw === null) return null;
    const stored = JSON.parse(raw) as Partial<StoredToken>;
    if (typeof stored.userId !== 'string' || typeof stored.token !== 'string' || typeof stored.expiresAt !== 'number') return null;
    if (stored.userId !== userId || stored.expiresAt - Date.now() <= REFRESH_MARGIN_MS) return null;
    return { userId: stored.userId, token: stored.token, expiresAt: stored.expiresAt };
  } catch {
    return null;
  }
}

/** Токен — в память и на диск: следующий запуск начнёт с него. */
function writeToken(userId: string, token: string, expiresAt: number): void {
  current = { userId, token, expiresAt };
  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify({ userId, token, expiresAt }));
  } catch {
    // Хранилище недоступно — токен проживёт до закрытия приложения.
  }
}

/** Забыть токен: выход, отзыв ключа устройства или 401. */
function forgetToken(): void {
  current = null;
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Хранилища нет — чистить нечего.
  }
}

/** Живой токен: из памяти или с диска. Протухший — заменит `obtain`. */
function validToken(): StoredToken | null {
  if (current !== null && current.expiresAt - Date.now() > REFRESH_MARGIN_MS) return current;
  const { userId } = useUserStore.getState();
  if (userId.length === 0) return null;
  const stored = readToken(userId);
  if (stored !== null) current = stored;
  return stored;
}

const hasCode = (error: unknown, code: string): boolean => error instanceof ApiClientError && error.code === code;

const authRequired = (): ApiClientError => new ApiClientError({ code: 'AUTH_REQUIRED', message: 'Войди по почте, чтобы продолжить' });

function post(input: OpenSession): Promise<AuthSession> {
  return apiRequest<AuthSession>('/api/auth/session', { method: 'POST', body: jsonBody(input) });
}

/** Сессия по ключу устройства. Ключа нет (гость) — `AUTH_REQUIRED`, без запроса. */
async function obtain(): Promise<AuthSession> {
  const { userId, deviceKey } = useUserStore.getState();
  if (deviceKey.length === 0 || userId.length === 0) throw authRequired();
  try {
    return await post({ userId, deviceKey });
  } catch (error) {
    if (!hasCode(error, 'AUTH_DEVICE_INVALID')) throw error;
    // Ключ отозван (выход на другом устройстве, база пересоздана) — снова гость:
    // экраны за входом отправят на экран входа.
    logger.warn('Ключ устройства не принят — нужен вход по почте');
    signOutLocally();
    throw authRequired();
  }
}

function token(): Promise<string> {
  const stored = validToken();
  if (stored !== null) return Promise.resolve(stored.token);
  // Один запрос на всех: десяток параллельных вызовов API ждут один токен.
  pending ??= obtain()
    .then((session) => {
      const store = useUserStore.getState();
      if (store.userId !== session.userId || store.deviceKey !== session.deviceKey || store.email !== session.email) {
        store.setIdentity(session.userId, session.deviceKey, session.email);
      }
      writeToken(session.userId, session.token, Date.parse(session.expiresAt));
      return session.token;
    })
    .finally(() => {
      pending = null;
    });
  return pending;
}

configureAuth({
  token,
  invalidate: (rejected) => {
    if (current?.token === rejected) forgetToken();
  },
});

/** Забыть сессию на устройстве: токен и ключ в сторе. */
function signOutLocally(): void {
  forgetToken();
  const { userId } = useUserStore.getState();
  useUserStore.getState().setIdentity(userId, '', null);
}

/**
 * Анонимный аккаунт этого устройства из прошлых версий — для слияния при
 * входе. Без ключа устройства аккаунт не отправляется: сервер не
 * закрепляет аккаунт по одному `userId` и нового здесь не заведёт.
 */
function legacyAnonymous(): { readonly userId: string; readonly deviceKey: string } | undefined {
  const { userId, deviceKey, email } = useUserStore.getState();
  if (email !== null || userId.length === 0 || deviceKey.length === 0) return undefined;
  return { userId, deviceKey };
}

/** Сессия с сервера — в стор и на диск: дальнейшие запросы идут от её аккаунта. */
function adopt(session: AuthSession): void {
  useUserStore.getState().setIdentity(session.userId, session.deviceKey, session.email);
  writeToken(session.userId, session.token, Date.parse(session.expiresAt));
}

/** Код входа на почту; ответ — через сколько секунд можно запросить снова. */
export function requestLoginCode(email: string, language: LanguageCode): Promise<{ readonly resendInSeconds: number }> {
  return apiRequest<{ readonly resendInSeconds: number }>('/api/auth/request-code', { method: 'POST', body: jsonBody({ email, language }) });
}

/**
 * Код из письма → вход. Согласия с галочек экрана входа уходят вместе с
 * кодом: без принятого соглашения сервер вход не примет. Анонимный аккаунт
 * этого устройства из прошлых версий (ключ — доказательство, что он свой)
 * уходит на сервер: его прогресс вливается в аккаунт с почтой.
 */
export async function verifyLoginCode(email: string, code: string, consents: Consents): Promise<AuthSession> {
  const anonymous = legacyAnonymous();
  const session = await apiRequest<AuthSession>('/api/auth/verify-code', { method: 'POST', body: jsonBody({ email, code, consents, anonymous }) });
  adopt(session);
  return session;
}

/** Локальные данные аккаунта; тема и язык интерфейса — настройки устройства, остаются. */
// `micPermissionAsked` — новый ученик на этом устройстве увидит объяснение микрофона один раз.
const ACCOUNT_KEYS = ['aptile-user', 'aptile-learning', 'aptile-guide', 'micPermissionAsked', 'micPermissionGranted'] as const;

/** Сколько ждать отзыв ключа: выход не должен зависеть от сети. */
const LOGOUT_TIMEOUT_MS = 4000;

/**
 * Выход: ключ устройства отзывается на сервере (если сеть позволяет — не
 * дольше `LOGOUT_TIMEOUT_MS`), токен и локальные данные аккаунта стираются,
 * приложение перезапускается на экране приветствия — гостем.
 */
export async function logout(): Promise<void> {
  const { userId, deviceKey } = useUserStore.getState();
  if (deviceKey.length > 0) {
    await apiRequest('/api/auth/logout', { method: 'POST', body: jsonBody({ userId, deviceKey }), timeoutMs: LOGOUT_TIMEOUT_MS }).catch(() => undefined);
  }
  forgetToken();
  useUserStore.getState().reset();
  resetAnalytics();
  try {
    for (const key of ACCOUNT_KEYS) localStorage.removeItem(key);
    sessionStorage.clear();
  } catch {
    // Хранилище недоступно — перезапуск всё равно начнёт с чистого листа.
  }
  // Перезапуск: память (токен, подгруженные уроки, сторы) — тоже с нуля.
  window.location.hash = '#/';
  window.location.reload();
}
